import React, { useState, useEffect } from "react";
import { Plus, Edit, Trash, Eye, EyeOff } from "lucide-react";
import api from "../../utils/Api";

export default function Empleados() {
  const [empleados, setEmpleados] = useState([]);
  const [filteredEmpleados, setFilteredEmpleados] = useState([]);
  const [offcanvasOpen, setOffcanvasOpen] = useState(false);
  const [selectedEmpleado, setSelectedEmpleado] = useState(null);
  const [formData, setFormData] = useState({
    dni: "",
    nombre: "",
    apellidos: "",
    email_usuario: "",
    telefono_usuario: "",
    rol: "empleado",
    estado: "activo",
    password: "",
    password_confirmation: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [empleadoToDelete, setEmpleadoToDelete] = useState(null);

  // Pagination
  const [page, setPage] = useState(1);
  const itemsPerPage = 5;

  // Alert state
  const [alert, setAlert] = useState({ type: "", message: "" });

  const fetchEmpleados = async () => {
    try {
      const { data } = await api.get("/usuarios");
      setEmpleados(data);
      setFilteredEmpleados(data);
    } catch (error) {
      console.error(error);
      setAlert({ type: "danger", message: "Error al cargar empleados" });
    }
  };

  useEffect(() => {
    fetchEmpleados();
  }, []);

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    const filtered = empleados.filter(
      (emp) =>
        emp.nombre.toLowerCase().includes(value) ||
        emp.apellidos.toLowerCase().includes(value)
    );
    setFilteredEmpleados(filtered);
    setPage(1);
  };

  const openOffcanvas = (empleado = null) => {
    setSelectedEmpleado(empleado);
    setFormData(
      empleado
        ? {
            dni: empleado.dni,
            nombre: empleado.nombre,
            apellidos: empleado.apellidos,
            email_usuario: empleado.email,
            telefono_usuario: empleado.telefono,
            rol: empleado.rol,
            estado: empleado.estado,
            password: "",
            password_confirmation: "",
          }
        : {
            dni: "",
            nombre: "",
            apellidos: "",
            email_usuario: "",
            telefono_usuario: "",
            rol: "empleado",
            estado: "activo",
            password: "",
            password_confirmation: "",
          }
    );
    setOffcanvasOpen(true);
  };

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedEmpleado) {
        await api.put(`/usuarios/${selectedEmpleado.id}`, formData);
        setAlert({ type: "success", message: "Empleado actualizado correctamente" });
      } else {
        await api.post("/usuarios", formData);
        setAlert({ type: "success", message: "Empleado creado correctamente" });
      }
      fetchEmpleados();
      setOffcanvasOpen(false);
    } catch (error) {
      console.error(error);
      setAlert({
        type: "danger",
        message: error.response?.data?.message || "Error al guardar empleado",
      });
    }
  };

  const confirmDelete = (empleado) => {
    setEmpleadoToDelete(empleado);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!empleadoToDelete) return;
    try {
      await api.delete(`/usuarios/${empleadoToDelete.id}`);
      setAlert({ type: "success", message: "Empleado eliminado correctamente" });
      fetchEmpleados();
      setDeleteModalOpen(false);
      setEmpleadoToDelete(null);
    } catch (error) {
      console.error(error);
      setAlert({
        type: "danger",
        message: error.response?.data?.message || "Error al eliminar empleado",
      });
    }
  };

  const totalPages = Math.ceil(filteredEmpleados.length / itemsPerPage);
  const paginatedEmpleados = filteredEmpleados.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  return (
    <div className="container-fluid" style={{ backgroundColor: "#F5F5F5", minHeight: "100vh" }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 className="fs-3 fw-bold">Gestión de Empleados</h1>
        <button
          className="btn"
          style={{ backgroundColor: "#FF7A00", color: "#FFFFFF" }}
          onClick={() => openOffcanvas()}
        >
          <Plus size={18} className="me-1" /> Nuevo
        </button>
      </div>

      {alert.message && (
        <div className={`alert alert-${alert.type} alert-dismissible fade show`} role="alert">
          {alert.message}
          <button type="button" className="btn-close" onClick={() => setAlert({ message: "" })}></button>
        </div>
      )}

      <div className="mb-3">
        <input
          type="text"
          className="form-control w-50"
          placeholder="Buscar por nombre o apellido"
          onChange={handleSearch}
        />
      </div>

      <div className="table-responsive">
        <table className="table table-hover align-middle" style={{ backgroundColor: "#FFFFFF" }}>
          <thead style={{ backgroundColor: "#F5F5F5", color: "#2C2C2C" }}>
            <tr>
              <th>DNI</th>
              <th>Nombre</th>
              <th>Apellidos</th>
              <th>Email</th>
              <th>Teléfono</th>
              <th>Rol</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {paginatedEmpleados.length > 0 ? (
              paginatedEmpleados.map((emp) => (
                <tr key={emp.id}>
                  <td>{emp.dni}</td>
                  <td>{emp.nombre}</td>
                  <td>{emp.apellidos}</td>
                  <td>{emp.email}</td>
                  <td>{emp.telefono}</td>
                  <td>
                    <span
                      className="badge"
                      style={{
                        backgroundColor: emp.rol === "admin" ? "#FF7A00" : "#2C2C2C",
                        color: "#FFFFFF",
                      }}
                    >
                      {emp.rol}
                    </span>
                  </td>
                  <td>
                    <span
                      className="badge"
                      style={{
                        backgroundColor: emp.estado === "activo" ? "#FF7A00" : "#2C2C2C",
                        color: "#FFFFFF",
                      }}
                    >
                      {emp.estado}
                    </span>
                  </td>
                  <td>
                    <button
                      className="btn btn-sm me-2"
                      style={{ backgroundColor: "#FF7A00", color: "#FFFFFF" }}
                      onClick={() => openOffcanvas(emp)}
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      className="btn btn-sm"
                      style={{ backgroundColor: "#2C2C2C", color: "#FFFFFF" }}
                      onClick={() => confirmDelete(emp)}
                    >
                      <Trash size={16} />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center text-muted">
                  No hay empleados
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 10 }}>
          <button disabled={page === 1} onClick={() => setPage(page - 1)}>Anterior</button>

          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              style={{
                fontWeight: page === i + 1 ? "bold" : "normal",
                textDecoration: page === i + 1 ? "underline" : "none"
              }}
              onClick={() => setPage(i + 1)}
            >
              {i + 1}
            </button>
          ))}

          <button disabled={page === totalPages} onClick={() => setPage(page + 1)}>Siguiente</button>
        </div>
      )}


      {deleteModalOpen && (
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
             style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
             onClick={() => setDeleteModalOpen(false)}>
          <div className="bg-white p-4 rounded shadow" style={{ minWidth: "300px" }} onClick={(e) => e.stopPropagation()}>
            <h5 style={{ color: "#2C2C2C" }}>Confirmar Eliminación</h5>
            <p style={{ color: "#2C2C2C" }}>
              ¿Estás seguro que quieres eliminar a <strong>{empleadoToDelete?.nombre} {empleadoToDelete?.apellidos}</strong>?
            </p>
            <div className="d-flex justify-content-end gap-2">
              <button className="btn" style={{ backgroundColor: "#2C2C2C", color: "#FFFFFF" }} onClick={() => setDeleteModalOpen(false)}>Cancelar</button>
              <button className="btn" style={{ backgroundColor: "#FF7A00", color: "#FFFFFF" }} onClick={handleDelete}>Eliminar</button>
            </div>
          </div>
        </div>
      )}

      {offcanvasOpen && (
        <>
          <div className="offcanvas-backdrop fade show" onClick={() => setOffcanvasOpen(false)}></div>
          <div className="offcanvas offcanvas-end show" style={{ visibility: "visible" }}>
            <div className="offcanvas-header">
              <h5 className="offcanvas-title">{selectedEmpleado ? "Editar Empleado" : "Nuevo Empleado"}</h5>
              <button type="button" className="btn-close" onClick={() => setOffcanvasOpen(false)}></button>
            </div>
            <div className="offcanvas-body">
              <form onSubmit={handleSubmit}>
                {/* DNI, Nombre, Apellidos, Email */}
                <div className="row g-3 mb-3">
                  <div className="col">
                    <label style={{ color: "#2C2C2C" }}>DNI</label>
                    <input type="text" className="form-control" name="dni" value={formData.dni} onChange={handleChange} style={{ backgroundColor: "#FFFFFF", color: "#2C2C2C" }} required />
                  </div>
                  <div className="col">
                    <label style={{ color: "#2C2C2C" }}>Nombre</label>
                    <input type="text" className="form-control" name="nombre" value={formData.nombre} onChange={handleChange} style={{ backgroundColor: "#FFFFFF", color: "#2C2C2C" }} required />
                  </div>
                </div>
                <div className="row g-3 mb-3">
                  <div className="col">
                    <label style={{ color: "#2C2C2C" }}>Apellidos</label>
                    <input type="text" className="form-control" name="apellidos" value={formData.apellidos} onChange={handleChange} style={{ backgroundColor: "#FFFFFF", color: "#2C2C2C" }} required />
                  </div>
                  <div className="col">
                    <label style={{ color: "#2C2C2C" }}>Email</label>
                    <input type="email" className="form-control" name="email_usuario" value={formData.email_usuario} onChange={handleChange} style={{ backgroundColor: "#FFFFFF", color: "#2C2C2C" }} required />
                  </div>
                </div>

                {/* Rol y Estado */}
                <div className="row g-3 mb-3">
                  <div className="col">
                    <label style={{ color: "#2C2C2C" }}>Rol</label>
                    <select className="form-select" name="rol" value={formData.rol} onChange={handleChange} style={{ backgroundColor: "#FFFFFF", color: "#2C2C2C" }} required>
                      <option value="empleado">Empleado</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div className="col">
                    <label style={{ color: "#2C2C2C" }}>Estado</label>
                    <select className="form-select" name="estado" value={formData.estado} onChange={handleChange} style={{ backgroundColor: "#FFFFFF", color: "#2C2C2C" }} required>
                      <option value="activo">Activo</option>
                      <option value="inactivo">Inactivo</option>
                    </select>
                  </div>
                </div>

                {/* Contraseña */}
                <div className="row g-3 mb-3">
                  <div className="col position-relative">
                    <label style={{ color: "#2C2C2C" }}>Contraseña</label>
                    <div className="input-group">
                      <input type={showPassword ? "text" : "password"} className="form-control pe-5" name="password" value={formData.password} onChange={handleChange} style={{ backgroundColor: "#FFFFFF", color: "#2C2C2C" }} />
                      <span className="position-absolute top-50 end-0 translate-middle-y me-3" style={{ cursor: "pointer", color: "#2C2C2C" }} onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </span>
                    </div>  
                  </div>
                  <div className="col position-relative">
                    <label style={{ color: "#2C2C2C" }}>Confirmar Contraseña</label>
                    <div className="input-group">
                      <input type={showPasswordConfirm ? "text" : "password"} className="form-control pe-5" name="password_confirmation" value={formData.password_confirmation} onChange={handleChange} style={{ backgroundColor: "#FFFFFF", color: "#2C2C2C" }} />
                      <span className="position-absolute top-50 end-0 translate-middle-y me-3" style={{ cursor: "pointer", color: "#2C2C2C" }} onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}>
                        {showPasswordConfirm ? <EyeOff size={20} /> : <Eye size={20} />}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-end">
                  <button type="button" className="btn me-2" style={{ backgroundColor: "#2C2C2C", color: "#FFFFFF" }} onClick={() => setOffcanvasOpen(false)}>Cancelar</button>
                  <button type="submit" className="btn" style={{ backgroundColor: "#FF7A00", color: "#FFFFFF" }}>Guardar</button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
